import { hasError } from '@repo/shared/api/helpers'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { Switch } from '@repo/ui/switch'
import { cn } from '@repo/ui/utils'
import { FileCheckIcon, Loader2Icon, TriangleAlertIcon, UploadCloudIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { IoSparklesSharp } from 'react-icons/io5'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useLoadingMessages } from '@/hooks'
import { AvailableParams } from '@/routes/router'

export function Uploader<T>(
  props: (
    | {
        // Smart uploader
        isSmart: true
        onStoreFile?: (file?: File) => void
      }
    | object // basic uploader
  ) &
    Omit<UploaderProps<T>, 'onDrop'>,
) {
  if ('isSmart' in props && props.isSmart) {
    const { onUploaded, processFile, onStoreFile } = props
    return (
      <SmartUploader onUploaded={onUploaded} processFile={processFile} onStoreFile={onStoreFile} />
    )
  }
  const { onUploaded, processFile } = props
  return <BasicUploader onUploaded={onUploaded} processFile={processFile} />
}

type UploaderProps<T> = {
  onUploaded: (response: T) => void
  processFile: (params: { organizationId: string; file: File }) => Promise<T>
  onDrop?: (file: File) => void
}

function useUploader<T>({ onUploaded, processFile, onDrop }: UploaderProps<T>) {
  const { organizationId } = useParams<AvailableParams>()
  //fixme : uploader for sudo : don't have organizationId
  // if (!organizationId)
  //   throw new Error('Organization ID is required for file upload')
  const [status, setStatus] = useState<'idle' | 'uploading' | 'end'>('idle')

  const handleProcessFile = useCallback(
    async (file: File) => {
      setStatus('uploading')

      const response = await processFile({
        organizationId: organizationId ?? '',
        file,
      })

      if (hasError(response)) {
        setStatus('idle')
        return toast.error(`Error processing file ${JSON.stringify(response)}`)
      }

      setStatus('end')
      onUploaded(response)
    },
    [processFile, onUploaded, organizationId],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onError: (err) => {
      console.error(err)
    },
    disabled: status === 'uploading' || status === 'end',
    maxFiles: 1,
    onDrop: (files) => {
      const file = files[0]
      if (files.length === 0 || !file) return
      if (onDrop) onDrop(file)
      void handleProcessFile(file)
    },
  })

  return {
    getInputProps,
    getRootProps,
    handleProcessFile,
    status,
    setStatus,
  }
}

function BasicUploader<T>({ onUploaded, processFile }: UploaderProps<T>) {
  const { getRootProps, getInputProps, status } = useUploader<T>({
    onUploaded,
    processFile,
  })
  const { tc } = useT()
  return (
    <div className="flex flex-1 flex-col gap-4">
      <div {...getRootProps()} className="flex flex-1">
        <Button className="w-full" disabled={status !== 'idle'}>
          {status === 'uploading' ? (
            <Loader2Icon className="size-5 animate-spin" />
          ) : (
            <UploadCloudIcon className="size-5" />
          )}{' '}
          {tc('dragOrUploadFile')}
        </Button>
        <input {...getInputProps()} />
      </div>
    </div>
  )
}

function SmartUploader<T>({
  onUploaded,
  processFile,
  onStoreFile,
}: UploaderProps<T> & {
  onStoreFile?: (file?: File) => void
}) {
  const { tc } = useT()
  const [file, setFile] = useState<File>()
  const [isFileStored, setIsFileStored] = useState(false)

  const { getRootProps, getInputProps, status, setStatus, handleProcessFile } = useUploader<T>({
    onUploaded,
    processFile,
    onDrop: setFile,
  })

  useEffect(() => {
    onStoreFile?.(isFileStored ? file : undefined)
  }, [isFileStored, file])

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div {...getRootProps()} className="flex flex-1">
        <div className="flex w-full items-center gap-2">
          {status === 'uploading' ? (
            <AiMessage uploading={status === 'uploading'} />
          ) : status === 'end' ? (
            <>
              <Button disabled variant="secondary" className="disabled:opacity-100">
                <IoSparklesSharp className="size-4" />
                {tc('aiAssistant', { colon: true })} {tc('smartScanDone')}
              </Button>

              <Button
                variant="highlight"
                className="w-fit"
                onClick={() => {
                  setStatus('idle')
                  if (!file) return
                  void handleProcessFile(file)
                }}
              >
                <IoSparklesSharp className="size-4" />
                {tc('regenerate', { cfl: true })}
              </Button>
            </>
          ) : (
            <Button variant="highlight" className="w-full">
              <IoSparklesSharp className="size-4" /> {tc('dragOrUploadFile')}
            </Button>
          )}
        </div>

        <input {...getInputProps()} />
      </div>

      <div className="flex select-none flex-col gap-2">
        <div
          className={cn(
            'flex items-center gap-2',
            isFileStored ? 'text-green-600' : 'text-orange-400',
          )}
        >
          {isFileStored ? (
            <>
              <FileCheckIcon className="size-5" />
              {tc('fileIsStored')}
            </>
          ) : (
            <>
              <TriangleAlertIcon className="size-5" />
              {tc('fileIsNotStored')}
            </>
          )}
        </div>
        {onStoreFile && (
          <div
            onClick={() => setIsFileStored(!isFileStored)}
            className="text-primary flex cursor-pointer items-center gap-2 px-px underline-offset-1 hover:underline"
          >
            <Switch checked={isFileStored} />
            {tc('storeFile', { cfl: true })}
          </div>
        )}
      </div>
    </div>
  )
}

function AiMessage({ uploading }: { uploading: boolean }) {
  const { tc } = useT()
  const AiMessage = useLoadingMessages(['readingFile', 'writingSummary'], uploading, 500)
  return (
    <Button disabled variant="secondary" className="disabled:opacity-100">
      <IoSparklesSharp className="size-4 animate-pulse" />
      {tc('aiAssistant', { colon: true })} {AiMessage}
    </Button>
  )
}
